import React, { useContext, useEffect, useState } from "react";
import {
  Alert, Button, Col, Input as RSInput,
  Nav, NavItem, NavLink,
  Row, TabContent, TabPane,
} from "reactstrap";
import DebounceInput from "react-debounce-input";
import { BsMoonFill, BsSunFill } from "react-icons/bs";
import Switch from "react-switch";
import NumberFormat from "react-number-format";
import Select, { components } from "react-select";
import classnames from "classnames";
import _ from "lodash";
import { api, helpers, UserContext, constants, storage } from "../utils";
import { DesktopContainer } from "../layouts";

const { THEMES } = constants;
const TABS = {
  DETAILS: "Profile Details",
  PASSWORD: "Credentials",
};

const Input = ({ ...rest }) => (
  <components.Input {...rest} autoComplete="off" />
);

// signIn stores the user in localStorage ("currentUser"), so the Credentials tab's typed passwords must be blanked
// first - otherwise they sit there in plain text until sign-out and refill the fields on the next visit.
const withoutPasswords = (u) => ({ ...u, oldPassword: "", newPassword: "", newPasswordAgain: "" });

const sameEmail = (a, b) =>
  _.trim(a || "").toUpperCase() === _.trim(b || "").toUpperCase();

export default function UserProfile() {
  const device =
    storage.getItem("deviceType") === "undefined"
      ? {
        label: "Desktop or Laptop",
        value: 0,
        isMobile: false,
        hasScanner: false,
      }
      : JSON.parse(storage.getItem("deviceType"));
  const userCtx = useContext(UserContext);
  const [activeTab, setActiveTab] = useState(TABS.DETAILS);
  const [defaultTimezone, setDefaultTimezone] = useState(null);
  const [message, setMessage] = useState(null);
  const [selectedDevice, setSelectedDevice] = useState(device);
  const [timezoneList, setTimezoneList] = useState([]);
  const [user, setUser] = useState(userCtx.currentUser);
  const [working, setWorking] = useState(false);
  // Changing your own email needs your current password (the server refuses it otherwise). The password
  // is kept apart from `user` so it never reaches userCtx.signIn and the stored profile.
  const [loadedEmail, setLoadedEmail] = useState(userCtx.currentUser?.email || "");
  const [currentPassword, setCurrentPassword] = useState("");
  const emailChanged = !sameEmail(user.email, loadedEmail);

  useEffect(() => {
    setWorking(true);
    api
      .getTimezones()
      .then((response) => {
        const tzList = _.chain(response)
          .map((tz) => {
            return { label: tz, value: tz };
          })
          .sortBy((x) => {
            return x.label;
          })
          .value();
        setTimezoneList(tzList);
        if (tzList.length > 0) {
          const dftz = _.find(
            tzList,
            (t) => t.label === constants.DEFAULT_TIMEZONE
          );
          setDefaultTimezone(dftz);
        }
      })
      .catch(api.catchHandler)
      .finally(() => setWorking(false));
  }, []);

  useEffect(() => {
    setUser(userCtx.currentUser);
    setLoadedEmail(userCtx.currentUser?.email || "");
  }, [userCtx.currentUser]);

  function handleChange(fieldName, fieldValue) {
    let newUser = Object.assign({}, user);
    newUser[fieldName] = fieldValue;
    setUser(newUser);
  }

  function onSubmit() {
    if (working) return;
    let warnings = [];
    if (!user.firstName || _.trim(user.firstName).length < 2) {
      warnings.push("A valid first name is required.");
    }
    if (!user.lastName || _.trim(user.lastName).length < 2) {
      warnings.push("A valid last name is required.");
    }
    if (!user.email || !helpers.emailIsValid(user.email)) {
      warnings.push("A valid email is required.");
    }
    if (emailChanged && !currentPassword) {
      warnings.push("Enter your current password to change your email address.");
    }
    if (warnings.length) {
      setMessage({
        text: warnings.join(" "),
        flavor: "danger",
      });
      return;
    } else {
      setMessage(null);
    }
    setWorking(true);
    userCtx.setSavedDevice(selectedDevice);
    // Built on a copy - `user` state is never mutated. The select stores the plain timezone value; an older stored
    // profile may still hold an option object.
    const timezone = user.timezone
      ? (typeof user.timezone === "string" ? user.timezone : user.timezone.label)
      : defaultTimezone?.label;
    const saved = withoutPasswords({ ...user, timezone, username: user.email });
    // The Credentials tab's typed passwords never ride on this request; the current password rides only when the
    // email changes, and never lands in the user state.
    const payload = emailChanged ? { ...saved, currentPassword } : saved;
    api
      .post("User/SaveProfile", payload)
      .then((response) => {
        if (response.data.success) {
          setMessage({
            text: "Your profile has been updated successfully!",
            flavor: "success",
          });
          setCurrentPassword("");
          setLoadedEmail(saved.email);
          setTimeout(() => userCtx.signIn(saved), 3000);
        } else {
          setMessage({ text: response?.data?.message, flavor: "danger" });
        }
      })
      .catch(api.catchHandler)
      .finally(() => setWorking(false));
  }

  function onUpdatePassword() {
    let warnings = [];
    if (!user.newPassword || _.trim(user.newPassword).length < 8) {
      warnings.push(
        "New password must be at least 8 characters."
      );
    }
    if (!user.oldPassword || _.trim(user.oldPassword).length < 8) {
      warnings.push(
        "Existing password must be at least 8 characters."
      );
    }
    if (!user.newPasswordAgain || _.trim(user.newPasswordAgain).length < 8) {
      warnings.push("Please confirm your new password.");
    }
    if (user.newPassword !== user.newPasswordAgain) {
      warnings.push(
        "Your new password and password confirmation do not match."
      );
    }
    if (user.newPassword === user.oldPassword) {
      warnings.push("Your new password and old password must be different.");
    }
    if (warnings.length) {
      setMessage({
        text: warnings.join(" "),
        flavor: "danger",
      });
      return;
    } else {
      setMessage(null);
    }
    setWorking(true);
    api
      .post("User/UpdatePassword", {
        password: user.newPassword,
        oldPassword: user.oldPassword,
      })
      .then((response) => {
        if (response.data.success) {
          setMessage({
            text: "Your password has been updated successfully!",
            flavor: "success",
          });
          const cleared = withoutPasswords(user);
          setUser(cleared);
          setTimeout(() => userCtx.signIn(cleared), 3000);
        } else {
          setMessage({ text: response?.data?.message, flavor: "danger" });
        }
      })
      .catch(api.catchHandler)
      .finally(() => setWorking(false));
  }

  return (
    <DesktopContainer screenName={"Edit Your Profile"}>
      {message ? (
        <Alert className={message.flavor}>{message.text}</Alert>
      ) : null}
      <Nav tabs>
        <NavItem className="profileTab">
          <NavLink
            className={classnames(
              { active: activeTab === TABS.DETAILS },
              "ms-5 profileTab"
            )}
            onClick={() => setActiveTab(TABS.DETAILS)}
          >
            {TABS.DETAILS}
          </NavLink>
        </NavItem>
        <NavItem className="pointer">
          <NavLink
            className={classnames(
              { active: activeTab === TABS.PASSWORD },
              "ms-1"
            )}
            onClick={() => setActiveTab(TABS.PASSWORD)}
          >
            {TABS.PASSWORD}
          </NavLink>
        </NavItem>
      </Nav>
      <TabContent activeTab={activeTab} className="userProfileContent pb-5">
        <TabPane tabId={TABS.DETAILS}>
          <form autoComplete="off">
            <Row className="pt-3 px-5">
              <Col className="userProfile mt-3" xs="12" md="3">
                First Name{" "}
                {!user.firstName?.length ? helpers.requiredStar() : null}
                <DebounceInput
                  type="text"
                  name="firstName"
                  id="firstName"
                  onChange={(event) =>
                    handleChange(event.target.name, event.target.value)
                  }
                  value={user.firstName || ""}
                  className="form-control"
                  debounceTimeout={300}
                />
              </Col>
              <Col className="userProfile mt-3" xs="12" md="3">
                Last Name{" "}
                {!user.lastName?.length ? helpers.requiredStar() : null}
                <DebounceInput
                  type="text"
                  name="lastName"
                  id="lastName"
                  onChange={(event) =>
                    handleChange(event.target.name, event.target.value)
                  }
                  value={user.lastName || ""}
                  className="form-control"
                  debounceTimeout={300}
                />
              </Col>
              <Col className="userProfile mt-3" xs="12" md="3">
                Email {!user.email?.length ? helpers.requiredStar() : null}
                <DebounceInput
                  type="text"
                  name="email"
                  id="email"
                  onChange={(event) =>
                    handleChange(event.target.name, event.target.value)
                  }
                  value={user.email || ""}
                  className="form-control"
                  debounceTimeout={300}
                />
              </Col>
              <Col className="userProfile mt-3" xs="12" md="3">
                Mobile Phone #
                <NumberFormat
                  className="form-control"
                  format="(###) ###-####"
                  mask="_"
                  name="mobilePhoneNumber"
                  id="mobilePhoneNumber"
                  maxLength="15"
                  onValueChange={(values) => {
                    // eslint-disable-next-line no-unused-vars
                    const { formattedValue, value } = values;
                    handleChange("mobilePhoneNumber", value);
                  }}
                  value={user.mobilePhoneNumber || ""}
                />
              </Col>
            </Row>
            {emailChanged ? (
              <Row className="px-5">
                <Col className="userProfile mt-3" xs="12" md={{ size: 3, offset: 6 }}>
                  Current Password{" "}
                  {!currentPassword ? helpers.requiredStar() : null}
                  <RSInput
                    type="password"
                    name="currentPassword"
                    id="currentPassword"
                    autoComplete="current-password"
                    onChange={(event) => setCurrentPassword(event.target.value)}
                    value={currentPassword}
                    className="form-control"
                  />
                  <small className="text-muted">Needed to change your email address.</small>
                </Col>
              </Row>
            ) : null}
            <Row className="px-5">
              <Col className="userProfile mt-3" xs="12" md="3">
                Timezone
                <Select
                  classNamePrefix="serviceThreadSelect"
                  options={timezoneList}
                  value={
                    _.find(timezoneList, (x) => x.value === user.timezone) ||
                    defaultTimezone
                  }
                  onChange={(option) => handleChange("timezone", option.value)}
                  key="timezoneSelect"
                  id="timezoneSelect"
                  name="profileTimezone"
                  components={{ Input }}
                />
              </Col>
              {user.isAnyCustomerTypeRole ? null : (
                <Col className="userProfile mt-3" xs="12" md="3">
                  Device Type
                  <Select
                    classNamePrefix="serviceThreadSelect"
                    options={constants.DEVICE_TYPES}
                    value={selectedDevice}
                    onChange={(opt) => setSelectedDevice(opt)}
                  />
                </Col>
              )}
              <Col className="userProfile mt-3" xs="12" md="3">
                Theme <br />
                <Switch
                  checked={user.typeOfTheme === THEMES.lightTheme.value}
                  onChange={(checked) => {
                    userCtx.changeTheme(
                      checked ? THEMES.lightTheme.label : THEMES.darkTheme.label
                    );
                    handleChange(
                      "typeOfTheme",
                      checked ? THEMES.lightTheme.value : THEMES.darkTheme.value
                    );
                  }}
                  onColor="#F9D71C"
                  onHandleColor="#F9D71C"
                  offHandleColor="#888"
                  uncheckedIcon={false}
                  checkedIcon={false}
                  uncheckedHandleIcon={
                    <BsMoonFill
                      className="themeSwitchIcon"
                      style={{ color: "#F9D71C" }}
                    />
                  }
                  checkedHandleIcon={
                    <BsSunFill
                      className="themeSwitchIcon"
                      style={{ color: "#888" }}
                    />
                  }
                  handleDiameter={30}
                  height={20}
                  width={48}
                />
              </Col>
            </Row>
            <Row className="px-5">
              <Col xs={{ size: 1, offset: 11 }}>
                <Button
                  className="shredButton float-end"
                  onClick={onSubmit}
                  disabled={working}
                >
                  Save
                </Button>
              </Col>
            </Row>
          </form>
        </TabPane>
        <TabPane className="profileTab" tabId={TABS.PASSWORD}>
          <Row className="justify-content-center">
            <Col className="userProfile" xs="6">
              <Row className="mt-3">
                <Col xs="12">
                  <div className="my-1">
                    Old Password{" "}
                    {!user.oldPassword ? helpers.requiredStar() : null}
                  </div>
                  <RSInput
                    type="password"
                    name="oldPassword"
                    id="oldPassword"
                    onChange={(event) =>
                      handleChange(event.target.name, event.target.value)
                    }
                    value={user.oldPassword || ""}
                    className="form-control"
                  />
                </Col>
              </Row>
              <Row className="mt-3">
                <Col className="userProfile" xs="12">
                  <div className="my-1">
                    New Password{" "}
                    {!user.newPassword ? helpers.requiredStar() : null}
                  </div>
                  <RSInput
                    type="password"
                    name="newPassword"
                    id="newPassword"
                    onChange={(event) =>
                      handleChange(event.target.name, event.target.value)
                    }
                    value={user.newPassword || ""}
                    className="form-control"
                  />
                </Col>
              </Row>
              <Row className="mt-3">
                <Col className="userProfile" xs="12">
                  <div className="my-1">
                    Retype New Password{" "}
                    {!user.newPasswordAgain ? helpers.requiredStar() : null}
                  </div>
                  <RSInput
                    type="password"
                    name="newPasswordAgain"
                    id="newPasswordAgain"
                    onChange={(event) =>
                      handleChange(event.target.name, event.target.value)
                    }
                    value={user.newPasswordAgain || ""}
                    className="form-control"
                  />
                </Col>
              </Row>
              <Row className="mt-3">
                <Col className="userProfile text-right" xs="12">
                  <Button
                    className="shredButton float-end"
                    onClick={() => onUpdatePassword()}
                    disabled={working}
                  >
                    Update Password
                  </Button>
                </Col>
              </Row>
            </Col>
          </Row>
        </TabPane>
      </TabContent>
    </DesktopContainer>
  );
}
